import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getEnvironmentsforUser } from '../../api/documentAPI';
import { useChatEnvironmentAtom } from '../../atoms/chat/useChatEnvironmentAtom';
import  {useTabItemIdAtom } from '../../atoms/chat/useTabItemIdAtom';
import { ALL_FOLDERS } from '../../atoms/chat/useChatEnvironmentAtom';
import  useAuth  from '../../hooks/useAuth';

import tabConfig from '../../config/tabConfig';
import useCookie from '../../hooks/useCookie';
import { use } from 'react';

const Tabs = ({ selectedTab, handleTabClick }) => {

    const { auth } = useAuth();
    const { t } = useTranslation('global');
    const [, setEnvCookie] = useCookie("ENV");
    const [, setTitCookie] = useCookie("TIT");
    const [tabItemId, setTabItemId] = useTabItemIdAtom(); 
    const [chatEnvironmentId, setChatEnvironmentId] = useChatEnvironmentAtom();
    const [environments, setEnvironments] = useState([]);

    //const tabsWithDropdown = tabConfig.tabs.filter(tab => tab.dropdownItems.length > 0);
    //const shouldRenderTabs = tabConfig.tabs.length > 1 || tabsWithDropdown.length > 0;

    const tabsRef = useRef(null);
    const measureRef = useRef(null);
    const [gliderStyle, setGliderStyle] = useState({});
    const [dropdownWidth, setDropdownWidth] = useState(null);
    // „Všetky moje priečinky“ (hľadanie naprieč priečinkami) — ponúka sa pri 2 a viac priečinkoch, predvolené
    const multiFolder = environments.length > 1;
    const isValidEnv = (id) => (multiFolder && String(id) === ALL_FOLDERS)
      || environments.some(env => String(env.Id) === String(id));
    const defaultEnvId = (envs) => (envs.length > 1 ? ALL_FOLDERS : String(envs[0].Id));
    const selectedEnvName = String(chatEnvironmentId) === ALL_FOLDERS
      ? t('allMyFolders')
      : environments.find(env => String(env.Id) === String(chatEnvironmentId))?.Name || '';

    const clickedTab = tabConfig.tabs.find(tab => tab.intent === selectedTab);

    const filterTabsByUserAccess = (tabs, accesses) => {
      if (!accesses) return [];
      const accessNames = accesses.map(a => a.name);
      return tabs.filter(tab => accessNames.includes(tab.accessKey));
    };

    const userAccessibleTabs = filterTabsByUserAccess(tabConfig.tabs, auth.uac);

    const handleTabItemChange = (event) => {
      const selectedValue = event.target.value;
      setTitCookie("TIT", event.target.value);
      setTabItemId(selectedValue);
    }

    const handleSelectEnvironment = (event) => {
      const selectedValue = event.target.value;
      setEnvCookie("ENV", event.target.value);
      setChatEnvironmentId(selectedValue);
    };
    
    useEffect(()=> {

      // Check if current tab is still accessible
      const currentTabIsAccessible = userAccessibleTabs.some(tab => tab.intent === selectedTab);
      if (!currentTabIsAccessible && userAccessibleTabs.length > 0) {
          handleTabClick(userAccessibleTabs[0].intent);
          return; // Exit early since we're changing tabs
      }

      if (selectedTab === 'PromptEmbeddings') {
        getEnvironmentsforUser().then((data) => {
            setEnvironments(data);

            if (data.length > 0 && !chatEnvironmentId) {
              setChatEnvironmentId(defaultEnvId(data));
            }
        });
      }

      if (clickedTab?.dropdownItems?.length > 0) {
          const firstItemId = clickedTab.dropdownItems[0].id;
          setTabItemId(firstItemId);
      }
    },[selectedTab]);

    // Zapamätaný priečinok (ENV cookie / otvorená stará konverzácia) už nemusí existovať alebo
    // k nemu používateľ stratil prístup -> predvolený výber (všetky priečinky / jediný priečinok).
    useEffect(() => {
      if (selectedTab !== 'PromptEmbeddings' || environments.length === 0 || !chatEnvironmentId) return;
      if (!isValidEnv(chatEnvironmentId)) {
        const fallback = defaultEnvId(environments);
        setChatEnvironmentId(fallback);
        setEnvCookie("ENV", fallback);
      }
    }, [environments, chatEnvironmentId, selectedTab]);

    // Responzívna šírka dropdownu podľa dĺžky vybraného názvu prostredia (meriame skrytým spanom)
    useLayoutEffect(() => {
      if (selectedTab === 'PromptEmbeddings' && measureRef.current) {
        const textWidth = measureRef.current.offsetWidth;
        setDropdownWidth(Math.min(Math.max(textWidth + 28, 70), 240)); // + miesto na šípku, clamp
      } else {
        setDropdownWidth(null);
      }
    }, [selectedTab, selectedEnvName, environments]);

    // Glider sleduje vybraný tab (taby majú teraz premenlivú šírku)
    useLayoutEffect(() => {
      const positionGlider = () => {
        const checked = tabsRef.current?.querySelector('input[type="radio"]:checked + label.tab');
        if (!checked) return;
        setGliderStyle({ left: `${checked.offsetLeft}px`, width: `${checked.offsetWidth}px` });
      };
      positionGlider();
      window.addEventListener('resize', positionGlider);
      return () => window.removeEventListener('resize', positionGlider);
    }, [selectedTab, dropdownWidth, tabItemId, chatEnvironmentId, environments, userAccessibleTabs.length]);

    if (userAccessibleTabs.length === 0) {
      return null;
    }

    return (
      <div className='tab-menu-bar'>
      {userAccessibleTabs.length > 0 && (
        <div className="tabs-wrapper">
          <div className="tabs" ref={tabsRef}>
            {userAccessibleTabs.map(tab => (
              <React.Fragment key={tab.radio}>

                <input
                  type="radio"
                  id={tab.radio}
                  name="tabs"
                  checked={selectedTab === tab.intent}
                  onChange={() => handleTabClick(tab.intent)}
                />

                <label className="tab" htmlFor={tab.radio}>
                  <span className="material-symbols-outlined mobileOnly">{tab.icon}</span>
                  <span className='pcOnly'>{t(tab.label)}</span>
                  
                  {selectedTab === tab.intent && (
                    tab.intent === 'PromptEmbeddings' ? (
                      environments.length > 0 && (
                      <div className='dropdown-container'>
                        <select
                          value={chatEnvironmentId}
                          onChange={handleSelectEnvironment}
                          className='tab-dropdown'
                          style={dropdownWidth ? { width: dropdownWidth } : undefined}
                        >
                          {multiFolder && <option value={ALL_FOLDERS}>{t('allMyFolders')}</option>}
                          {environments.map((env, index) => (
                            <option key={index} value={env.Id}>
                              {env.Name}
                            </option>
                          ))}
                    </select>
                  </div>
                  )
                 ) : (
                  tab.dropdownItems?.length > 0 && (
                      <div className="dropdown-container">
                        <select
                          value={tabItemId || '' }
                          onChange={handleTabItemChange}
                          className="tab-dropdown" 
                        >
                          {tab.dropdownItems.map(item => (
                            <option key={item.id} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      )
                    )
                  )}
                </label>
              </React.Fragment>
            ))}
            <span className="glider" style={gliderStyle}/>
          </div>
        </div>
      )}
      <span ref={measureRef} className="dropdown-measure" aria-hidden="true">{selectedEnvName}</span>
    </div>
  )
};

export default Tabs;